import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Dialog from '@cobliteam/ui/components/Dialog';
import Stepper, { StepperStepsSwitch, useStepperContext } from '@cobliteam/ui/components/Stepper';
import Typography from '@cobliteam/ui/components/Typography';
import Checkbox from '@cobliteam/ui/form/Checkbox';
import Label from '@cobliteam/ui/form/Label';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { useState } from 'react';

export interface FaceIdOnboardingModalProps {
  open: boolean;
  driverName: string;
  /** Fired on the final "Vincular fotos ao motorista" CTA. Carries the "don't show again" checkbox value. */
  onStart: (dontShowAgain: boolean) => void;
  /** Fired when the modal is dismissed without finishing (X / overlay / esc). Carries the checkbox value. */
  onClose: (dontShowAgain: boolean) => void;
}

const STEPS = [{ id: 'how' }, { id: 'process' }, { id: 'action' }];
const STEP_IDS = STEPS.map((s) => s.id);

const StepDots = () => {
  const { current } = useStepperContext();
  const activeIndex = STEP_IDS.indexOf(current.id);
  return (
    <div className='flex flex-row gap-2'>
      {STEP_IDS.map((id, index) => (
        <div key={id} className={cn('size-2 rounded-full', index === activeIndex ? 'bg-primary' : 'bg-gray-light')} />
      ))}
    </div>
  );
};

const StepHow = (_props: { id: string }) => {
  const { t } = useTranslation('drivers');
  return (
    <div className='flex flex-col gap-4'>
      <div className='flex items-center justify-center gap-2 rounded-lg bg-primary-3 py-7'>
        <Icon name='camera' size='lg' color='primary' />
        <Icon name='user' size='lg' className='text-primary-8' />
      </div>
      <Dialog.Title>{t('face_id.onboarding.step1.title')}</Dialog.Title>
      <Typography variant='text-md' color='secondary'>
        {t('face_id.onboarding.step1.body')}
      </Typography>
    </div>
  );
};

const StepProcess = (_props: { id: string }) => {
  const { t } = useTranslation('drivers');
  const items = [t('face_id.onboarding.step2.item1'), t('face_id.onboarding.step2.item2'), t('face_id.onboarding.step2.item3'), t('face_id.onboarding.step2.item4')];
  return (
    <div className='flex flex-col gap-4'>
      <Dialog.Title>{t('face_id.onboarding.step2.title')}</Dialog.Title>
      <ol className='flex flex-col gap-4'>
        {items.map((item, index) => (
          <li key={item} className='flex items-center gap-2'>
            <span className='flex size-9 shrink-0 items-center justify-center rounded-full bg-primary-2 font-semibold text-primary text-sm'>{index + 1}</span>
            <Typography variant='text-md' color='secondary'>
              {item}
            </Typography>
          </li>
        ))}
      </ol>
    </div>
  );
};

const StepAction = ({ driverName }: { id: string; driverName: string }) => {
  const { t } = useTranslation('drivers');
  return (
    <div className='flex flex-col gap-4'>
      <Dialog.Title>{t('face_id.onboarding.step3.title')}</Dialog.Title>
      <Typography variant='text-md' color='secondary'>
        {t('face_id.onboarding.step3.body', { driverName })}
      </Typography>
    </div>
  );
};

interface OnboardingFooterProps {
  dontShowAgain: boolean;
  setDontShowAgain: (value: boolean) => void;
  onStart: (dontShowAgain: boolean) => void;
}

const OnboardingFooter = ({ dontShowAgain, setDontShowAgain, onStart }: OnboardingFooterProps) => {
  const { t } = useTranslation('drivers');
  const { isFirst, isLast, next, prev } = useStepperContext();

  return (
    <Dialog.Footer className='flex items-center justify-between gap-2.5'>
      <div className='flex items-center gap-2'>
        <Checkbox id='faceid-onboarding-dont-show' checked={dontShowAgain} onCheckedChange={(checked) => setDontShowAgain(checked === true)} />
        <Label htmlFor='faceid-onboarding-dont-show'>{t('face_id.onboarding.step1.dont_show_again')}</Label>
      </div>

      <div className='flex gap-2'>
        <Button variant='outlined' disabled={isFirst} onClick={() => prev()}>
          {t('face_id.onboarding.back')}
        </Button>
        {isLast ? (
          <Button startIcon={<Icon name='link' />} onClick={() => onStart(dontShowAgain)}>
            {t('face_id.onboarding.start')}
          </Button>
        ) : (
          <Button onClick={() => next()}>{t('face_id.onboarding.next')}</Button>
        )}
      </div>
    </Dialog.Footer>
  );
};

const FaceIdOnboardingModal = ({ open, driverName, onStart, onClose }: FaceIdOnboardingModalProps) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  // Step state lives in the Stepper; it resets on the parent's keyed/conditional remount per open.
  const handleOpenChange = (next: boolean) => {
    if (!next) onClose(dontShowAgain);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content>
        <Stepper defineSteps={STEPS}>
          <Dialog.Header>
            <StepDots />
            <Dialog.Close />
          </Dialog.Header>

          <Dialog.Body className='flex flex-col'>
            <StepperStepsSwitch stateStep='how'>
              <StepHow id='how' />
              <StepProcess id='process' />
              <StepAction id='action' driverName={driverName} />
            </StepperStepsSwitch>
          </Dialog.Body>

          <OnboardingFooter dontShowAgain={dontShowAgain} setDontShowAgain={setDontShowAgain} onStart={onStart} />
        </Stepper>
      </Dialog.Content>
    </Dialog>
  );
};

export default FaceIdOnboardingModal;
